import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { Dialog } from "primereact/dialog";
import TabHeader from "../common/TabHeader";
import { Button } from "primereact/button";
import UserSearch from "../common/UserSearch";
import Loading from "../common/Loading";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";

export default function DialogAddBatchController({ visible, batchId, closeDialog }) {
    const { requestAPI, showToast } = useAppContext();

    const [selectedUser, setSelectedUser] = useState(null);
    const [assigning, setAssigning] = useState();
    const [controllersLoading, setControllersLoading] = useState();
    const [controllers, setControllers] = useState([]);

    const controllerUserIds = useMemo(() => new Set((controllers || []).map((controller) => Number(controller.user_id))), [controllers]);

    useEffect(() => {
        requestAPI({
            requestPath: `batches/${batchId}/controllers`,
            requestMethod: "GET",
            setLoading: setControllersLoading,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setControllers(data);
                }
            },
        });
    }, [batchId, requestAPI]);

    const addController = useCallback(() => {
        if (!selectedUser?.id) return;

        requestAPI({
            requestPath: `batches/${batchId}/controllers`,
            requestMethod: "POST",
            requestPostBody: { user_id: selectedUser.id },
            setLoading: setAssigning,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Add Controller !", life: 2000 }),
            onResponseReceieved: (added, responseCode) => {
                if (added && responseCode === 201) {
                    showToast({ severity: "success", summary: "Added", detail: "Controller Added To Batch", life: 1000 });
                    setControllers((prev) => [...(prev || []), added]);
                    setSelectedUser(null);
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: added?.error || "Failed To Add Controller !", life: 2000 });
                }
            },
        });
    }, [batchId, requestAPI, selectedUser, showToast]);

    return (
        <Dialog
            pt={{ content: { className: "overflow-visible" } }}
            header="Add Batch Controller"
            visible={visible}
            className="w-11"
            onHide={() => {
                setSelectedUser(null);
                closeDialog();
            }}
        >
            <TabHeader className="pt-3" title="Add Controller" highlights={["Search users who can manage this batch"]} />

            {controllersLoading ? <Loading /> : null}

            <UserSearch
                className="w-full mt-5"
                value={selectedUser}
                onChange={setSelectedUser}
                excludeUserIds={controllerUserIds}
                disabled={assigning || controllersLoading}
                floatLabel
                label="Controller"
                inputId="batch-controller-search"
            />

            <HasRequiredAuthority requiredAuthority={AUTHORITIES.MANAGE_BATCH_CONTROLLER}>
                <Button
                    className="mt-3"
                    label="Add Controller"
                    icon="pi pi-plus"
                    severity="warning"
                    disabled={!selectedUser?.id || assigning || controllersLoading}
                    loading={assigning}
                    onClick={addController}
                />
            </HasRequiredAuthority>
        </Dialog>
    );
}
